import { buildSearchParams } from '@utils/data-fetching/searchParams';
import { AuthRedirectQueryParams, AuthUserTokenQueryParams } from '@auth/constants';

const STRIP_KEYS: ReadonlySet<string> = new Set<string>([...Object.values(AuthRedirectQueryParams), ...Object.values(AuthUserTokenQueryParams)]);

/**
 * Joins a base path that may already carry a query string with an additional `?…` query
 * fragment. `buildSearchParams` always prefixes with `?`; concatenating naively when the
 * base already has a `?` produces `?a=1?b=2`, which browsers parse as a single query value
 * `a=1?b=2`. This helper detects and uses `&` instead.
 */
const joinQuery = (base: string, extras: string): string => {
  if (!extras) return base;
  const tail = extras.startsWith('?') ? extras.slice(1) : extras;
  if (!tail) return base;
  return `${base}${base.includes('?') ? '&' : '?'}${tail}`;
};

/**
 * Builds the post-auth redirect URL from the landing page's search parameters.
 *
 * Reads `targetPath` (react) or `_target_path` (symfony, legacy) as the redirect base
 * and re-appends the remaining query params with sensitive auth tokens stripped.
 *
 * Auth tokens (`login_token`, `user_token`, `impersonate_token`) are stripped because
 * they would otherwise round-trip into the final URL bar, browser history, Referer
 * header, and server access logs — leaking a single-use credential. The landing page
 * consumes the token once via the form's `userToken` prop; after that the value is
 * spent and sensitive.
 *
 * `searchParamsAsObject` is fed by `useSearchParams()`, which has already percent-
 * decoded its values once — a second `decodeURIComponent` here would turn nested
 * literals like `%26` into `&` and silently split one query value into two params,
 * and would throw `URIError` on malformed `%` sequences. Values are treated as
 * already-decoded.
 *
 * Returns `null` when neither redirect param is present, so callers can fall back to
 * their default destination.
 */
export const buildRedirectUrl = (searchParamsAsObject: Record<string, unknown>): string | null => {
  const symfonyRedirect = searchParamsAsObject[AuthRedirectQueryParams.symfony];
  const reactRedirect = searchParamsAsObject[AuthRedirectQueryParams.react];

  const sanitizedRest = Object.fromEntries(Object.entries(searchParamsAsObject).filter(([key]) => !STRIP_KEYS.has(key)));
  const paramsForRedirect = buildSearchParams(sanitizedRest);

  if (typeof symfonyRedirect === 'string') {
    return joinQuery(symfonyRedirect, paramsForRedirect);
  }
  if (typeof reactRedirect === 'string') {
    return joinQuery(reactRedirect, paramsForRedirect);
  }
  return null;
};
